import { State, Action, StateContext, Actions, ofActionDispatched, Store, Selector } from '@ngxs/store';
import { Sccs1ConnectionInfoUpdated, Sccs1ConnectionChangedShowInfo, Sccs1ConnectionChanged } from './sccs1-connection.actions';
import { Injectable } from '@angular/core';
import { ToastUserInfoNotification } from '../../notification/notification-toastr/notification-toastr.actions';
import { NotificationType } from '../../../samwebDescription/samweb/notification/notificationType';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { auditTime, distinctUntilChanged, skip, switchMap } from 'rxjs/operators';
import _ from 'lodash';

export interface Sccs1ConnectionStateModel {
   availableConnections: string[];
   unavailableConnections: string[];
   connectionWithSuccessfulLogons: string[];
   connectionInUse: string;
   sccs1SessionInUse: string;
   isFirstConnectionChangedNotification: boolean;
   preventSwitchOver: boolean;
}

@State<Sccs1ConnectionStateModel>({
   name: 'sccs1Connection',
   defaults: {
      availableConnections: [],
      unavailableConnections: [],
      connectionWithSuccessfulLogons: [],
      connectionInUse: null,
      sccs1SessionInUse: null,
      isFirstConnectionChangedNotification: true,
      preventSwitchOver: false,
   },
})
@Injectable()
export class Sccs1ConnectionState {
   constructor(private translateService: TranslateService, store: Store, actions$: Actions) {
      actions$.pipe(ofActionDispatched(Sccs1ConnectionChanged), distinctUntilChanged(), auditTime(650)).subscribe(() => {
         store.dispatch(new Sccs1ConnectionChangedShowInfo());
      });
   }

   @Selector([Sccs1ConnectionState])
   public static getAvailableConnections(state: Sccs1ConnectionStateModel): string[] {
      return state.availableConnections;
   }

   @Selector([Sccs1ConnectionState])
   public static getUnavailableConnections(state: Sccs1ConnectionStateModel): string[] {
      return state.unavailableConnections;
   }

   @Selector([Sccs1ConnectionState.getAvailableConnections, Sccs1ConnectionState.getUnavailableConnections])
   public static getAllConnections(availableConnections: string[], unavailableConnections: string[]): { name: string; isAvailable: boolean }[] {
      const allConnections: { name: string; isAvailable: boolean }[] = [];
      _.forEach(availableConnections, (name) => allConnections.push({ name: name, isAvailable: true }));
      _.forEach(unavailableConnections, (name) => allConnections.push({ name: name, isAvailable: false }));
      return allConnections;
   }

   @Selector([Sccs1ConnectionState])
   public static getConnectionWithSuccessfulLogons(state: Sccs1ConnectionStateModel): string[] {
      return state.connectionWithSuccessfulLogons;
   }

   @Selector([Sccs1ConnectionState])
   public static getSccs1ConnectionInUse(state: Sccs1ConnectionStateModel): string {
      return state.connectionInUse;
   }

   @Selector([Sccs1ConnectionState])
   public static getSccs1SessionInUse(state: Sccs1ConnectionStateModel): string {
      return state.sccs1SessionInUse;
   }

   @Selector([Sccs1ConnectionState])
   public static isPreventSwitchOverActive(state: Sccs1ConnectionStateModel): boolean {
      return state.preventSwitchOver;
   }

   @Selector([Sccs1ConnectionState])
   public static anyConnectionInUse(state: Sccs1ConnectionStateModel): boolean {
      return !_.isNil(state.connectionInUse);
   }

   @Action(Sccs1ConnectionInfoUpdated)
   public sccs1ConnectionInfoUpdated(ctx: StateContext<Sccs1ConnectionStateModel>, action: Sccs1ConnectionInfoUpdated) {
      const previousConnection = ctx.getState().connectionInUse;
      ctx.patchState({
         availableConnections: action.connectionChangedRealTimeData.availableConnections,
         unavailableConnections: action.connectionChangedRealTimeData.unavailableConnections,
         connectionInUse: action.connectionChangedRealTimeData.connectionInUse,
         sccs1SessionInUse: action.connectionChangedRealTimeData.sccs1SessionId,
         preventSwitchOver: action.connectionChangedRealTimeData.preventSwitchOver,
         connectionWithSuccessfulLogons: action.connectionChangedRealTimeData.connectionWithSuccessfulLogons,
      });

      const currentConnection = ctx.getState().connectionInUse;

      if (previousConnection === currentConnection) {
         return;
      }
      return ctx.dispatch(new Sccs1ConnectionChanged(currentConnection));
   }

   @Action(Sccs1ConnectionChangedShowInfo)
   public sccs1ConnectionChangedShowInfo(ctx: StateContext<Sccs1ConnectionStateModel>, action: Sccs1ConnectionChangedShowInfo) {
      const currentConnection = ctx.getState().connectionInUse;

      const isFirstConnectionChangedNotification = ctx.getState().isFirstConnectionChangedNotification;
      ctx.patchState({
         isFirstConnectionChangedNotification: false,
      });

      if (_.isEmpty(currentConnection)) {
         return combineLatest([
            this.translateService.get('notification.default.sccs1.connection_changed_title'),
            this.translateService.get('notification.default.sccs1.no_connection_summary'),
         ]).pipe(switchMap(([title, summary]) => ctx.dispatch(new ToastUserInfoNotification(NotificationType.WARN, title, summary))));
      }

      if (isFirstConnectionChangedNotification) {
         return;
      }

      return combineLatest([
         this.translateService.get('notification.default.sccs1.connection_changed_title'),
         this.translateService.get('notification.default.sccs1.connection_changed_summary', { serverInUse: currentConnection }),
      ]).pipe(switchMap(([title, summary]) => ctx.dispatch(new ToastUserInfoNotification(NotificationType.SUCCESS, title, summary))));
   }
}
